import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { getInternalUsersForAssigningLeads } from "services/userService";
import { updateLeadOwner, bulkLeadActionDispatcher } from "services/leadsService";
import { toast } from "sonner";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { AssignmentOwnerSelector } from "components/shared/form/AssignmentOwnerSelector";
import { invalidateTimelineCache } from "hooks/useTimeline";

const AssignOwnerModal = ({ isOpen, onClose, leadData, onOwnerChange, currentOwnerName, selectAllLeads = false }) => {
  // selectedOwners: [{ ownerId: string, isPrimary: boolean }]
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);

  const saveRef = useRef(null);

  // Fetch users when modal opens and seed from existing owners
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      seedFromLeadData();
    }
  }, [isOpen]);

  const seedFromLeadData = () => {
    // Pre-populate from leadData.Owners if available
    const existing = Array.isArray(leadData?.Owners) ? leadData.Owners : [];
    if (existing.length > 0) {
      setSelectedOwners(
        existing.map((o) => ({
          ownerId: String(o.OwnerId|| ""),
          isPrimary: !!o.isPrimary,
        }))
      );
    } else {
      setSelectedOwners([]);
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await getInternalUsersForAssigningLeads();
      if (response.success) {
        setUsers(response.data || []);
      } else {
        toast.error("Failed to load users", {
          description: response.error || "Unable to fetch users for assignment",
        });
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOwners.length) {
      toast.error("Please select at least one user");
      return;
    }

    // If already assigned, show confirmation
    if (currentOwnerName && currentOwnerName !== "Not Assigned") {
      setShowReassignConfirm(true);
      return;
    }

    await performAssignment();
  };

  const performAssignment = async () => {
    setIsSubmitting(true);
    setShowReassignConfirm(false);
    try {
      let result;

      // Build the owners payload: [{ isPrimary, OwnerId }]
      const ownersPayload = selectedOwners.map((o) => ({
        OwnerId: o.ownerId,
        IsPrimary: o.isPrimary,
      }));

      const isBulkAction = selectAllLeads || Array.isArray(leadData?.id);

      if (isBulkAction) {
        const bulkActionPayload = {
          ...(leadData?.filters || {}),
          includeAllFiltered: selectAllLeads,
          leadIds: selectAllLeads ? undefined : leadData?.id,
          actionType: 1, // 1 for Assign Owner
          Owners: ownersPayload,
        };
        result = await bulkLeadActionDispatcher(bulkActionPayload);
      } else {
        result = await updateLeadOwner(leadData?.id, ownersPayload);
      }

      if (result.success) {
        toast.success(result.message || "Lead user assigned successfully");
        invalidateTimelineCache(leadData?.id);

        if (onOwnerChange) {
          await onOwnerChange({ Owners: ownersPayload });
        }

        onClose();
      } else {
        toast.error(result.error || "Failed to assign user");
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error("Failed to assign user");
    } finally {
      setIsSubmitting(false);
    }
  };

  // AssignmentOwnerSelector uses idField='ownerId' by default;
  // our users have userId — we remap for the selector
  const usersForSelector = users.map((u) => ({
    ...u,
    ownerId: String(u.userId),
  }));

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            {/* Header */}
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <UserCircleIcon className="h-5 w-5 text-blue-600" />
                Assign User
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Scrollable Content */}
            <div className="flex flex-col overflow-visible px-4 py-4 sm:px-5 max-h-[calc(85vh-140px)]">

              {/* Current Owners Display */}
              {currentOwnerName && (
                <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2">
                    <UserCircleIcon className="size-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Current Owner(s)</p>
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">{currentOwnerName}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="size-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                    <span className="ml-2 text-sm text-gray-500">Loading users...</span>
                  </div>
                ) : (
                  <AssignmentOwnerSelector
                    id="assign-user-selector"
                    label={currentOwnerName ? "Reassign to" : "Lead User(s)"}
                    placeholder="Select user..."
                    data={usersForSelector}
                    value={selectedOwners}
                    onChange={setSelectedOwners}
                    idField="ownerId"
                    displayField="fullName"
                  />
                )}

                {selectedOwners.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-dark-400">
                    💡 Hover over a selected user in the dropdown and click <span className="font-semibold">Set Primary</span> to mark them as the primary user.
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-4 dark:border-dark-600 sm:px-5">
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting || selectedOwners.length === 0}
                >
                  {isSubmitting ? "Assigning..." : "Assign User"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      {/* Reassign Confirmation Modal */}
      <ConfirmModal
        show={showReassignConfirm}
        state="pending"
        onClose={() => setShowReassignConfirm(false)}
        onOk={performAssignment}
        confirmLoading={isSubmitting}
        messages={{
          pending: {
            title: "Reassign Lead?",
            description: `This lead is already assigned to ${currentOwnerName}. Do you want to reassign it?`,
            actionText: "Yes, Reassign",
          }
        }}
        buttonColor="primary"
      />
    </Transition>
  );
};

export default AssignOwnerModal;
